import { Box, CustomPagination, MUIDataTable, Typography } from 'web-library';
import React, { useState } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { fallbackDashString } from 'web-library/utils/StringUtils';

import { makeStyles } from 'tss-react/mui';

import { IndividualRecipientObject } from '../../../api-refactored/manage-notifications/useGetNotificationViewDetails';

const useStyles = makeStyles()(() =>
({
  paginationContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const tableColumns = (data: IndividualRecipientObject[], featureValue: string, t: TFunction<'translation'>) => [
  {
    name: 'indexNo',
    label: t('announcementsNotifications->>ViewDetails.IndexNo'),
    options: {
      sort: false,
      setCellProps: () => ({ style: { paddingTop: '20px' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <Typography>{fallbackDashString(data[dataIndex].indexNo)}</Typography>
      ),
    },
  },
  {
    name: 'nricFinPassportNo',
    label: t('announcementsNotifications->>ViewDetails.NricFinPassportNo'),
    options: {
      sort: false,
      setCellProps: () => ({ style: { paddingTop: '20px' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <Typography>{data[dataIndex].nricFinPassport}</Typography>
      ),
    },
  },
  {
    name: 'name',
    label: t('announcementsNotifications->>ViewDetails.Name'),
    options: {
      setCellProps: () => ({ style: { paddingTop: '20px' } }),
      customBodyRenderLite: (dataIndex: number) => <Typography>{data[dataIndex].name}</Typography>,
    },
  },
  {
    name: 'email',
    label: featureValue === 'SMS' ? t('announcementsNotifications->>ViewDetails.MobileNo') : t('announcementsNotifications->>ViewDetails.Email'),
    options: {
      sort: false,
      setCellProps: () => ({ style: { paddingTop: '20px' } }),
      customBodyRenderLite: (dataIndex: number) => (
        <Typography>{data[dataIndex].emailMobile}</Typography>
      ),
    },
  },
];

const TableFooter = ({
  classes,
  individualRecipients,
  pageNumber,
  pageSize,
  setPageNumber,
  setPageSize,
}: {
  classes: Record<string,string>;
  individualRecipients?: IndividualRecipientObject[];
  pageNumber: number;
  pageSize: number;
  setPageNumber: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
}) => (
  <Box className={classes.paginationContainer}>
    <CustomPagination
      dataLength={individualRecipients?.length || 0}
      page={pageNumber}
      rowsPerPage={pageSize}
      rowsOptions={[5, 10, 20, 40, 50]}
      setPage={(currentPage: number) => setPageNumber(currentPage)}
      setRowsPerPage={(numberOfRows: number) => {
        setPageNumber(0);
        setPageSize(numberOfRows);
      }}
    />
  </Box>
);

interface RecipientDataTableProps {
  individualRecipients?: IndividualRecipientObject[];
  featureValue: string;
}

const RecipientDataTable = ({ individualRecipients, featureValue }: RecipientDataTableProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(5);

  return (
    <>
      <MUIDataTable
        columns={tableColumns(individualRecipients || [], featureValue, t)}
        data={individualRecipients || []}
        emptyDataLabel={t('announcementsNotifications->>Listing.EmptyDataTableMessage')}
        options={{
          pagination: true,
          sort: true,
          selectToolbarPlacement: 'none',
          selectableRows: 'none',
          serverSide: false,
          page: pageNumber,
          rowsPerPage: pageSize,
          onChangePage: (currentPage: number) => setPageNumber(currentPage),
          onChangeRowsPerPage: (numberOfRows: number) => setPageSize(numberOfRows),
        }}
        components={{
          TableFooter: () =>
            TableFooter({
              classes,
              individualRecipients,
              pageNumber,
              pageSize,
              setPageNumber,
              setPageSize,
            }),
        }}
        rowsPerPageOptions={[5, 10, 20, 40, 50]}
      />
    </>
  );
};

export default RecipientDataTable;
